import Vue from 'vue'
import { API_URL } from '@/config'

export const sessions = {

  namespaced: true,

  state: {
    filters: [
      { column: 'id', label: 'id', options: { type: false }, value: '', status: true },
      { column: 'code', label: 'code', options: { type: false }, value: '', status: true },
      { column: 'title', label: 'title', options: { type: false }, value: '', status: true },
    ],
    options: {
      id: { label: 'id', element: 'text' },
      code: { label: 'code', element: 'text' },
      title: { label: 'title', element: 'text' },
    },
    presets: [],
    columns: [],
    sessions: [],
    sort: {
      column: 'title',
      columns: [
        { column: 'title', order: 'asc' },
        { column: 'id', order: 'asc' },
      ],
      order: 'asc'
    },
    total: 0,
    subtotal: 0,
    offset: 0,
  },

  getters: {
    filters: state => state.filters,
    options: state => state.options,
    presets: state => state.presets,
    columns: state => state.columns,
    sessions: state => state.sessions,
    sort: state => state.sort,
    total: state => state.total,
    subtotal: state => state.subtotal,
    offset: state => state.offset,
  },

  mutations: {
    setFilters(state, filters) { state.filters = filters },
    setOptions(state, options) { state.options = options },
    setPresets(state, presets) { state.presets = presets },
    setColumns(state, columns) { state.columns = columns },
    setDays(state, days) {
      state.options['day_id'] = {
        column: 'day_id',
        format: 'date',
        label: 'day',
        date: true,
        element: 'select',
        options: days.reduce((carry, day) => carry.concat([{ value: day.id, label: 'day', name: day.date }]), []),
      }
    },
    setRooms(state, params) {
      state.options['room_id'] = {
        column: 'room_id',
        format: null,
        label: 'room',
        element: 'select',
        options: params.rooms.reduce((carry, room) => carry.concat([{ value: room.id, label: 'room', name: room['name_' + params.language] }]), []),
      }
    },
    setSessions(state, sessions) { state.sessions = state.sessions.concat(sessions) },
    setSort(state, sort) { state.sort = sort },
    setTotal(state, total) { state.total = total },
    setSubtotal(state, subtotal) { state.subtotal = subtotal },
    setOffset(state, offset) { state.offset = offset },
    clearFilters(state) {
      state.filters = [
        { column: 'id', label: 'id', options: { type: false }, value: '', status: true },
        { column: 'code', label: 'code', options: { type: false }, value: '', status: true },
        { column: 'title', label: 'title', options: { type: false }, value: '', status: true },
      ];
      state.options = {
        'id': { label: 'id', element: 'text' },
        'code': { label: 'code', element: 'text' },
        'title': { label: 'title', element: 'text' },
      };
    },
    clearList(state) {
      state.sessions = [];
      state.total = 0;
      state.subtotal = 0;
      state.offset = 0;
    },
  },

  actions: {

    getOptions: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\Session'],
      filter_display: true,
    }}),

    getDays: () => Vue.axios.get(API_URL + '/days', { params: {
      sorts: [
        { 'column': 'days.order', 'order': 'asc'}
      ],
    }}),

    getRooms: () => Vue.axios.get(API_URL + '/rooms', { params: {
      sorts: [
        { 'column': 'rooms.order', 'order': 'asc'}
      ],
    }}),

    getColumns: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\Session'],
      list_display: true,
    }}),

    getSessions: ({ getters }) => Vue.axios.get(API_URL + '/sessions', { params: {
      filters: getters.filters,
      offset: getters.offset,
      sorts: getters.sort.columns,
    }}),

    async getList({ rootState, state, getters, commit, dispatch }) {
      const [options, days, rooms, columns, sessions] = await Promise.all([
        dispatch('getOptions'),
        dispatch('getDays'),
        dispatch('getRooms'),
        dispatch('getColumns'),
        dispatch('getSessions'),
      ])
      .catch(() => {
        commit('setProgress', false, { root: true });
      });
      commit('setProgress', false, { root: true });
      commit('setOptions', options.data['App\\Models\\Session'].filters);
      commit('setPresets', options.data['App\\Models\\Session'].presets);
      commit('setDays', days.data.items);
      commit('setRooms', { rooms: rooms.data.items, language: rootState.language });
      commit('setColumns', columns.data['App\\Models\\Session']);
      commit('setSessions', sessions.data.items);
      commit('setTotal', sessions.data.total);
      commit('setSubtotal', sessions.data.subtotal);
      commit('setOffset', getters.sessions.length);
      state.options.conflict = {
        format: 'wording',
        label: 'conflicts',
        element: 'radio',
        custom: true,
        options: [
          { value: 'overlaps', name: 'overlaps' },
          { value: 'has_overlapping_participations', name: 'has_overlapping_participations' },
          { value: 'has_cleared_overlapping_participations', name: 'has_cleared_overlapping_participations' },
          { value: 'exceeds', name: 'exceeds' },
        ],
      };
      state.options.comment_status = {
        format: 'wording',
        label: 'comment_status',
        element: 'radio',
        custom: true,
        options: [
          { value: 'without', name: 'without' },
          { value: 'ready', name: 'ready' },
          { value: 'open', name: 'open' },
          { value: 'problem', name: 'problem' },
          { value: 'error', name: 'error' },
        ],
      };
      state.options.participation_status = {
        format: 'wording',
        label: 'participation_status',
        element: 'radio',
        custom: true,
        options: [
          { value: 'not_invited', name: 'not_invited' },
          { value: 'invited', name: 'invited' },
          { value: 'confirmed', name: 'confirmed' },
          { value: 'declined', name: 'declined' },
        ],
      };
    },

    async addSessions({ getters, commit, dispatch }) {
      const sessions = await dispatch('getSessions');
      commit('setProgress', false, { root: true });
      commit('setSessions', sessions.data.items);
      commit('setOffset', getters.sessions.length);
    },

    async sortSessions({ rootState, commit, dispatch, getters }, data) {
      if (!rootState.progress) {
        let order = getters.sort.column === data.column 
          ? getters.sort.order === 'asc' ? 'desc' : 'asc'
          : 'asc';
        let columns = data.columns.reduce((carry, column) => { 
          carry.push({ column: column, order: order });
          return carry;
        }, []);
        commit('setSort', { column: data.column, columns: columns, order: order });
        commit('clearList');
        await dispatch('getList');
      }
    },

    async toggleSession({ state, commit, dispatch }, params) {
      if (params.session.day_id && params.session.start_time && params.session.end_time && params.session.room_id) {
        const values = {};
        if (params.mode === 'program') {
          values.publish = !params.session.publish;
          values.publish_app = !params.session.publish;
        }
        if (params.mode === 'app') {
          values.publish_app = !params.session.publish_app;
        }
        await Vue.axios.put(API_URL + '/sessions/' + params.session.id, values);
        state.offset = 0;
        const sessions = await dispatch('getSessions');
        state.sessions = [];
        commit('setProgress', false, { root: true });
        commit('setSessions', sessions.data.items);
        commit('setOffset', state.sessions.length);
      }
    },

  }
}
